// WR-04: an endpoint that keeps answering 502/503/504 while the recovery ping
// succeeds would otherwise loop flag -> ping OK -> refetch -> 504 -> flag
// forever. Recovery may retry each errored key only a few times; the manual
// QueryError / route "Försök igen" button stays uncapped.

export const MAX_RECOVERY_REFETCHES = 2;

export type RecoveryCandidate = { queryHash: string; dataUpdatedAt: number };

export function createRecoveryBudget(
  maxAttempts: number = MAX_RECOVERY_REFETCHES,
) {
  const records = new Map<string, { count: number; lastAttemptAt: number }>();

  return {
    select(candidates: RecoveryCandidate[], now: number): string[] {
      const present = new Set(candidates.map((c) => c.queryHash));
      // Keys that recovered or unmounted are forgotten.
      for (const key of Array.from(records.keys())) {
        if (!present.has(key)) records.delete(key);
      }

      const allowed: string[] = [];
      for (const candidate of candidates) {
        let record = records.get(candidate.queryHash);
        // It succeeded since the last attempt: start over.
        if (record && candidate.dataUpdatedAt > record.lastAttemptAt) {
          records.delete(candidate.queryHash);
          record = undefined;
        }
        if (!record || record.count < maxAttempts) {
          records.set(candidate.queryHash, {
            count: (record?.count ?? 0) + 1,
            lastAttemptAt: now,
          });
          allowed.push(candidate.queryHash);
        }
      }
      return allowed;
    },
  };
}

export const queryRecoveryBudget = createRecoveryBudget();
export const routeRecoveryBudget = createRecoveryBudget();

// Per-path evidence that a route worked: resolved without an errored match and
// no error page shown for it since. Plain strings, so it needs no router types.
export function createRouteOutcomeLog() {
  const successAt = new Map<string, number>();
  const errorAt = new Map<string, number>();

  return {
    resolved(pathname: string, matchStatuses: string[], now: number): void {
      if (matchStatuses.some((status) => status === "error")) return;
      successAt.set(pathname, now);
    },
    errorShown(pathname: string, now: number): void {
      errorAt.set(pathname, now);
    },
    lastSuccessAt(pathname: string): number {
      const success = successAt.get(pathname) ?? 0;
      const error = errorAt.get(pathname) ?? 0;
      return success > error ? success : 0;
    },
  };
}

export const routeOutcomeLog = createRouteOutcomeLog();
